/**
 * Assets Hub Navigation Configuration
 * 
 * Uses @quotech/ui for unified navigation across all Quotech hubs.
 */

import {
  LayoutDashboard,
  Truck,
  CalendarCheck,
  Wrench,
  MapPin,
  BarChart,
  DollarSign,
  Bell,
  PieChart,
  Settings,
  Briefcase,
  Package,
  Plus,
} from 'lucide-react'
import type { NavItem, ExternalLink, CommandItem, ModuleConfig, HubId, QuickAddItem } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'

// =============================================================================
// MODULE CONFIG
// =============================================================================

export const MODULE_CONFIG: ModuleConfig = {
  id: 'assets' as HubId,
  name: 'Assets Hub',
  icon: Truck,
  accentColor: 'slate',
  accentGradient: 'from-slate-500 to-slate-600',
  baseUrl: 'https://assets.quotech.ai',
}

// =============================================================================
// MAIN NAVIGATION (Core Work)
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
  },
  {
    id: 'assets',
    label: 'Assets',
    icon: Truck,
    href: '/assets',
  },
  {
    id: 'bookings',
    label: 'Bookings',
    icon: CalendarCheck,
    href: '/bookings',
  },
  {
    id: 'maintenance',
    label: 'Maintenance',
    icon: Wrench,
    href: '/maintenance',
  },
  {
    id: 'tracking',
    label: 'Tracking',
    icon: MapPin,
    href: '/tracking',
  },
]

// =============================================================================
// SECONDARY NAVIGATION (Tools)
// =============================================================================

export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'utilisation',
    label: 'Utilisation',
    icon: BarChart,
    href: '/utilisation',
  },
  {
    id: 'costs',
    label: 'Cost per Asset',
    icon: DollarSign,
    href: '/costs',
  },
  {
    id: 'alerts',
    label: 'Alerts',
    icon: Bell,
    href: '/alerts',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: PieChart,
    href: '/reports',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

// =============================================================================
// EXTERNAL HUB LINKS
// =============================================================================

export const EXTERNAL_LINKS: ExternalLink[] = getExternalLinks('assets')

// =============================================================================
// QUICK ADD ITEMS (Header dropdown)
// =============================================================================

export const QUICK_ADD_ITEMS: QuickAddItem[] = [
  {
    id: 'new-asset',
    label: 'New Asset',
    icon: Plus,
    action: () => { window.location.href = '/assets/new' },
    shortcut: '⌘⇧A',
  },
  {
    id: 'new-booking',
    label: 'New Booking',
    icon: CalendarCheck,
    action: () => { window.location.href = '/bookings/new' },
  },
  {
    id: 'new-maintenance',
    label: 'New Maintenance',
    icon: Wrench,
    action: () => { window.location.href = '/maintenance/new' },
  },
]

// =============================================================================
// COMMAND PALETTE COMMANDS
// =============================================================================

export const COMMANDS: CommandItem[] = [
  // Actions
  {
    id: 'new-asset',
    label: 'New Asset',
    description: 'Add a new asset',
    icon: Plus,
    action: () => { window.location.href = '/assets/new' },
    keywords: ['create', 'add', 'asset', 'vehicle', 'equipment'],
    category: 'action',
  },
  {
    id: 'new-booking',
    label: 'New Booking',
    description: 'Book an asset',
    icon: CalendarCheck,
    action: () => { window.location.href = '/bookings/new' },
    keywords: ['book', 'reserve', 'asset'],
    category: 'action',
  },
  
  // Navigation
  {
    id: 'go-dashboard',
    label: 'Go to Dashboard',
    icon: LayoutDashboard,
    action: () => { window.location.href = '/' },
    keywords: ['navigate', 'home', 'dashboard'],
    category: 'navigation',
  },
  {
    id: 'go-assets',
    label: 'Go to Assets',
    icon: Truck,
    action: () => { window.location.href = '/assets' },
    keywords: ['navigate', 'assets', 'vehicles', 'equipment'],
    category: 'navigation',
  },
  {
    id: 'go-bookings',
    label: 'Go to Bookings',
    icon: CalendarCheck,
    action: () => { window.location.href = '/bookings' },
    keywords: ['navigate', 'bookings', 'reservations'],
    category: 'navigation',
  },
  {
    id: 'go-maintenance',
    label: 'Go to Maintenance',
    icon: Wrench,
    action: () => { window.location.href = '/maintenance' },
    keywords: ['navigate', 'maintenance', 'repairs'],
    category: 'navigation',
  },
  
  // Hub switching
  {
    id: 'switch-jobs',
    label: 'Switch to Jobs Hub',
    icon: Briefcase,
    action: () => { window.location.href = 'https://dashboard.quotech.ai' },
    keywords: ['hub', 'jobs', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-inventory',
    label: 'Switch to Inventory Hub',
    icon: Package,
    action: () => { window.location.href = 'https://inventory.quotech.ai' },
    keywords: ['hub', 'inventory', 'switch'],
    category: 'hub',
  },
]

// =============================================================================
// HUB LINKS (for Command Palette)
// =============================================================================

export const HUB_LINKS: ExternalLink[] = EXTERNAL_LINKS

// =============================================================================
// ALIASES (for backward compatibility with setupApi.ts)
// =============================================================================

export const navigationItems = NAV_ITEMS
export const moduleConfig = MODULE_CONFIG
export const secondaryNavItems = SECONDARY_NAV_ITEMS
export const externalLinks = EXTERNAL_LINKS
export const commands = COMMANDS
export const hubLinks = HUB_LINKS
